/**
 * OpenAI Realtime 2.1 audio input requirements:
 * WAV PCM, mono, 16-bit, 24 kHz
 */
export const OPENAI_REALTIME_SAMPLE_RATE = 24000;
export const OPENAI_REALTIME_CHANNELS = 1;
export const OPENAI_REALTIME_BIT_DEPTH = 16;

/** Silence after speech before auto-stop (ms) */
export const SILENCE_TIMEOUT_MS = 3000;

/** RMS threshold to consider speech (0..1 float domain) */
export const SPEECH_RMS_THRESHOLD = 0.015;

/** Consecutive frames above threshold to confirm speech started */
export const SPEECH_CONFIRM_FRAMES = 3;

/**
 * Converts a recorded audio Blob (e.g. webm/ogg) into a WAV Blob
 * matching OpenAI Realtime 2.1: PCM, mono, 16-bit, 24 kHz.
 */
export async function convertBlobToRealtimeWav(blob) {
  if (!blob || blob.size === 0) {
    throw new Error('Áudio vazio ou inválido.');
  }

  const arrayBuffer = await blob.arrayBuffer();
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const decodeCtx = new AudioCtx();

  let audioBuffer;
  try {
    audioBuffer = await decodeCtx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    await decodeCtx.close().catch(() => {});
  }

  const monoSamples = await resampleToMono(audioBuffer, OPENAI_REALTIME_SAMPLE_RATE);
  const wavBuffer = encodeWavPcm16(monoSamples, OPENAI_REALTIME_SAMPLE_RATE);

  return new Blob([wavBuffer], { type: 'audio/wav' });
}

/**
 * Builds a WAV Blob from Float32 PCM chunks at `sourceSampleRate`,
 * resampling to 24 kHz mono 16-bit when needed.
 */
export function pcmChunksToRealtimeWav(chunks, sourceSampleRate) {
  const merged = mergeFloat32Chunks(chunks);
  if (!merged.length) {
    throw new Error('Áudio vazio ou inválido.');
  }

  const samples =
    sourceSampleRate === OPENAI_REALTIME_SAMPLE_RATE
      ? merged
      : resampleLinear(merged, sourceSampleRate, OPENAI_REALTIME_SAMPLE_RATE);

  const wavBuffer = encodeWavPcm16(samples, OPENAI_REALTIME_SAMPLE_RATE);
  return new Blob([wavBuffer], { type: 'audio/wav' });
}

export function computeRms(samples) {
  if (!samples || !samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i];
    sum += v * v;
  }
  return Math.sqrt(sum / samples.length);
}

export function mergeFloat32Chunks(chunks) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

async function resampleToMono(audioBuffer, targetSampleRate) {
  const frameCount = Math.max(1, Math.ceil(audioBuffer.duration * targetSampleRate));
  const offline = new OfflineAudioContext(1, frameCount, targetSampleRate);
  const source = offline.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offline.destination);
  source.start(0);

  const rendered = await offline.startRendering();
  return rendered.getChannelData(0);
}

function resampleLinear(input, fromRate, toRate) {
  if (fromRate === toRate) return input;
  if (!input.length) return new Float32Array(0);

  const ratio = fromRate / toRate;
  const newLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(newLength);

  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * ratio;
    const i0 = Math.floor(srcIndex);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const t = srcIndex - i0;
    output[i] = input[i0] * (1 - t) + input[i1] * t;
  }

  return output;
}

/**
 * Encodes Float32 samples (-1..1) as little-endian 16-bit PCM WAV.
 */
function encodeWavPcm16(samples, sampleRate) {
  const numChannels = OPENAI_REALTIME_CHANNELS;
  const bitsPerSample = OPENAI_REALTIME_BIT_DEPTH;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return buffer;
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}
